import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Send } from 'lucide-react';

const TIMEFRAMES: [string, string][] = [
  ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['365', 'Last 12 months'], ['all', 'All time'],
];

export default function EmailDigestSender() {
  const [provinces, setProvinces] = useState<string[]>([]);
  const [emailEnabled, setEmailEnabled] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [province, setProvince] = useState('');
  const [timeframe, setTimeframe] = useState('90');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    axios.get('/api/library/provinces').then(r => setProvinces(r.data)).catch(() => {});
    axios.get('/api/reports/email-status').then(r => setEmailEnabled(r.data.enabled)).catch(() => setEmailEnabled(false));
  }, []);

  const send = async () => {
    setSending(true); setResult(null);
    const end = new Date().toISOString();
    const start = timeframe === 'all' ? '' : new Date(Date.now() - Number(timeframe) * 86400000).toISOString();
    try {
      const r = await axios.post('/api/reports/send-now', {
        recipients: email, province: province || undefined, start: start || undefined, end, model,
      });
      setResult({ ok: !!r.data.sent, msg: r.data.sent ? `Sent to ${email}.` : (r.data.reason || 'Not sent') });
    } catch (e: any) {
      setResult({ ok: false, msg: e.response?.data?.error || e.response?.data?.detail || 'Failed to send' });
    } finally { setSending(false); }
  };

  const inp = 'bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[7px] text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]';

  return (
    <div>
      <p className="text-[13px] text-[color:var(--text-secondary)] mb-4">
        Generate a NAM brief for a region &amp; timeframe and email it now. Powered by Postmark.
      </p>

      {emailEnabled === false && (
        <div className="text-[12px] text-[#f59e0b] mb-3">Email isn’t configured yet (set Postmark credentials in the server env). The form is disabled until then.</div>
      )}

      <div className="grid gap-[10px] [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))] items-end">
        <label className="flex flex-col gap-1 text-[12px] text-[color:var(--text-muted)]">Recipient email
          <input className={inp} type="email" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-[color:var(--text-muted)]">Province
          <select className={inp} value={province} onChange={e => setProvince(e.target.value)}>
            <option value="">All provinces</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-[color:var(--text-muted)]">Timeframe
          <select className={inp} value={timeframe} onChange={e => setTimeframe(e.target.value)}>
            {TIMEFRAMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-[color:var(--text-muted)]">Depth
          <select className={inp} value={model} onChange={e => setModel(e.target.value)}>
            <option value="claude-sonnet-4-6">Standard (Sonnet)</option>
            <option value="deep">Deep (Opus)</option>
          </select>
        </label>
        <button
          onClick={send}
          disabled={sending || !email.trim() || emailEnabled === false}
          className="inline-flex items-center justify-center gap-2 text-[13px] px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white hover:opacity-90 disabled:opacity-50"
        >
          {sending ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Generate &amp; send</>}
        </button>
      </div>

      {result && (
        <div className={`mt-3 text-[13px] inline-flex items-center gap-2 ${result.ok ? 'text-[color:var(--accent)]' : 'text-[#f472b6]'}`}>
          <Mail size={14} /> {result.msg}
        </div>
      )}
    </div>
  );
}
